/**
 * The eighteen designed screens of the driver application, and which of them this build draws.
 *
 * This list is not navigation chrome. **The design draws none** — no tab bar, no drawer, no back
 * arrow, and no control of any kind for moving between the eighteen screens. The screen switcher at
 * the top of each design file is the design file's own furniture, the same shape the ten console
 * design files use, and building it into the application would be building the catalogue rather than
 * the product. So the flow is what the design implies: readiness leads to the offer inbox, an offer
 * arrives, it is accepted or it goes. Recorded as Q-63.
 *
 * What the list is for is three things that do need one place: the address bar's `?page=`, so a
 * screen is addressable and can be compared against its design; the readiness answer choosing which
 * of its three screens to draw; and an honest statement of what is not built and why, which is what
 * a screen reached by a stale address is told.
 */

/** What a screen is waiting on, when it is not built. */
export interface ScreenWaiting {
  /** The drawn element with no source, in the design's own words. */
  readonly element: string;
  /** What the platform would have to publish. */
  readonly needs: string;
}

export interface DriverScreen {
  /** The `?page=` identifier. Lowercase and hyphens, which is also what the router matches on. */
  readonly id: string;
  /** The design file's own name for the screen, as `SCREEN_SPEC.md` heads it. */
  readonly title: string;
  /** Which design file draws it. */
  readonly design: "11 Driver App" | "15 WP14 Driver Onboarding and Earnings";
  /** The operation this build reads it from, or null when nothing published serves its subject. */
  readonly operation: string | null;
  readonly built: boolean;
  /** Empty when built. Otherwise every drawn element with no published source. */
  readonly waiting: readonly ScreenWaiting[];
}

const SHIFT_READINESS =
  "GET /v1/tenants/{tenantId}/driver-app/drivers/{driverId}/shift-readiness (EXP-003)";
const OFFER_STREAM =
  "GET /v1/tenants/{tenantId}/driver-app/drivers/{driverId}/offer-stream (EXP-003)";
const EARNINGS = "GET /v1/tenants/{tenantId}/driver-app/drivers/{driverId}/earnings (EXP-003)";

/**
 * `6 Offline queue`'s subject is this phone, and its two destinations.
 *
 * The only screen of the eighteen whose content is not a platform read, and necessarily so: the
 * queue exists because the platform has not been told about any of it yet. What is named here is
 * where the queue *goes*, which is the thing that was missing until
 * [ADR 0025](../../../docs/adr/0025-the-upload-service-gets-a-public-surface-and-its-own-device-scope.md)
 * published FIL-02 on `/v1`.
 */
const DEVICE_QUEUE =
  "this device's own queue, sent to POST /v1/device/scans (LOG-06) and POST /v1/tenants/{tenantId}/uploads (FIL-02)";

/**
 * `8 Proof`'s subject, which is one proof named in the address.
 *
 * A direct read of a domain service rather than EXP-003's, because EXP-003 publishes no proof read.
 * Recorded in `src/driver-api.ts` and as R-33, with the finding that `delivery-proof:read` is a
 * tenant-wide scope where ADR 0025 would have asked for an ownership column and a 404.
 */
const PROOF = "GET /v1/proofs/{proofId} (DEL-07)";

/**
 * `4 To pickup`'s subject: one delivery order, and the address its own field names.
 *
 * Two services and three hops, all of them published, none of them a composition: DEL-03 answers the
 * order by the identifier this screen was told, `order.pickupAddressId` is on that answer, and
 * [ADR 0035](../../../docs/adr/0035-an-address-is-a-record-geo-02-owns-and-re-resolves.md) made
 * that identifier resolvable to a place. GEO-04's distance is over the coordinate GEO-02 stored and
 * this device's own position, which DRV-06 is told about in the same breath — so the design's
 * `background location on` is a claim this application can make rather than a phrase it prints.
 *
 * The operation named is the **subject's**, which is the order. The other three are in
 * `src/driver-api.ts` beside it, and `src/pickup.ts` says why each is reached directly.
 */
const PICKUP = "GET /v1/tenants/{tenantId}/delivery-orders/{orderId} (DEL-03)";

/**
 * `5 Scan`'s subject: the scan this device made, and the parcel it was of.
 *
 * A **write** first, which is what makes this screen a sibling of `6 Offline queue` rather than of
 * the other eleven: the thing it draws is what LOG-06 did with a scan, and `outcome` is published on
 * the answer. LOG-02's parcel read answers the headline, and nothing on this screen is a composition.
 */
const SCAN =
  "POST /v1/device/scans (LOG-06) and GET /v1/tenants/{tenantId}/parcels/{parcelId} (LOG-02)";

/**
 * `Vehicle`'s subject: the vehicles assigned to this courier, and the papers of the active one.
 *
 * The read
 * [ADR 0034](../../../docs/adr/0034-a-drivers-vehicle-is-an-assignment-record-many-over-time.md)
 * served, and the one of the three that closed a screen's **every** absence at once. The listing's
 * `vehicle-assignment:self` arm is written into the route — it compares the stored `driverUserId`
 * against the verified token subject and answers 404 for another courier's identifier — which makes
 * it the only one of this application's direct reads whose bound is the service's rather than this
 * application's restraint. `GET /v1/vehicles/{vehicleId}` beside it has no such arm, and R-48
 * records that.
 */
const VEHICLE =
  "GET /v1/drivers/{driverId}/vehicle-assignments (DRV-04) and GET /v1/vehicles/{vehicleId} (DRV-04)";

/**
 * `Multi-stop job`'s subject: the plans this courier is carrying, and each stop's place.
 *
 * The read [ADR 0040](../../../docs/adr/0040-a-courier-finds-its-stop-plan-through-a-listing-bounded-by-its-own-subject.md)
 * served, and the shape of the absence is worth keeping. TRP-04 could already create a plan and
 * read one — and the read was keyed on a `planId` only the creator ever saw, so a courier's handset
 * had no way to name the plan it was carrying. That is the `GET /v1/sessions` trap `CLAUDE.md`
 * section 4 names, arriving from the other direction, and the sweep's own wording for it ("POST
 * exists; there is no GET") was wrong about which half was missing. R-53.
 *
 * The listing's `trip:stops:self` arm is written into the route: it compares each plan's stored
 * `driverUserId` against the verified token subject and answers 404 for another courier's
 * identifier. That makes it the second of this application's direct reads whose bound is the
 * service's rather than this application's restraint, after DRV-04's assignments.
 */
const STOP_PLAN =
  "GET /v1/tenants/{tenantId}/drivers/{driverId}/stop-plans (TRP-04) and GET /v1/addresses/{addressId} (GEO-02)";

/**
 * `Document upload`'s subject: this handset's own captures, and FIL-03's verdict on them.
 *
 * Two halves, and only the second is a platform read. What has left this phone and what has not is
 * device state — the same store `6 Offline queue` draws — and FIL-02's upload read is how a resumed
 * attempt learns that bytes it has no record of sending did in fact land (ADR 0025).
 *
 * FIL-03's job read is the one that was missing. It existed only under `/internal/v1`, which
 * [ADR 0029](../../../docs/adr/0029-the-gateway-is-api-01s-data-plane-and-refuses-the-workload-surface.md)
 * refuses to route, and is now published on `/v1` as a narrowed status projection under its own
 * scope — `docs/OPEN_WORK.md` item 2, in the shape ADR 0025 settled for FIL-02 next door. It
 * answers a status and a transformation and nothing else, so the object address of what a courier
 * photographed stays on the workload surface.
 */
const DOCUMENT_UPLOAD =
  "this device's own captures, sent to POST /v1/tenants/{tenantId}/uploads (FIL-02), with GET /v1/tenants/{tenantId}/jobs/{jobId} (FIL-03) for the blur and glare check";

/**
 * All eighteen, in the order the two design files list them.
 *
 * Fifteen are marked `built`. Eight of them are the eight EXP-003's own README names as the screens
 * its three operations serve; two arrived with ADR 0025 and do not read EXP-003 at all —
 * `6 Offline queue`'s subject is this handset, and `8 Proof`'s is one DEL-07 proof.
 *
 * The last three arrived on 8 October with two **decisions** rather than with routes, which is the
 * thing worth noticing about them: ADR 0034 made a courier's vehicle an assignment record and gave
 * `Vehicle` every value it draws, ADR 0035 gave the platform an address it owns and gave
 * `4 To pickup` its headline, and `5 Scan` became answerable when LOG-02 published the parcel a scan
 * was of. The entries below said, for several weeks, that those three were waiting on
 * `driverProfile.vehicleId`, on `orders.pickupAddressLabel` and on a parcel read — and the first two
 * of those field names are the ones ADR 0034 and ADR 0035 **refused to publish**, having decided
 * that the question was a record rather than a column. A `waiting` entry naming a field that will
 * never exist is a stale entry that reads as current, which is why these were corrected rather than
 * deleted quietly.
 *
 * The last two arrived on 10 October and, like those three, arrived with a **decision** each rather
 * than with a route somebody had been waiting for. `docs/OPEN_WORK.md` item 2 published FIL-03's
 * processing-job read on `/v1` as a narrowed status projection, which is the whole of
 * `Document upload`; and ADR 0040 gave a courier a listing of their own stop plans, which is the
 * whole of `Multi-stop job`. The second is the one worth re-reading: the entry here said it was
 * waiting on a stop-plan read, and a public stop-plan read **already existed** — keyed on an
 * identifier no handset ever sees. A `waiting` entry that names the wrong absence is worse than one
 * that names a stale field, because it sends the next reader to build the wrong thing. R-53.
 *
 * The three marked otherwise are not held back by a missing field: **nothing published answers their
 * subject at all**, which is a different thing and is why they are not drawn.
 * `tests/contract/mobile-screen-support.test.ts` measures the same ground from the outside, per
 * drawn element, and `tests/navigation.test.ts` checks this list against it so the two cannot drift
 * apart.
 */
export const driverScreens: readonly DriverScreen[] = [
  {
    id: "go-online",
    title: "1 Go online",
    design: "11 Driver App",
    operation: SHIFT_READINESS,
    built: true,
    waiting: [],
  },
  {
    id: "cannot-go-online",
    title: "1b Can't go online",
    design: "11 Driver App",
    operation: SHIFT_READINESS,
    built: true,
    waiting: [],
  },
  {
    id: "online",
    title: "2 Online",
    design: "11 Driver App",
    operation: OFFER_STREAM,
    built: true,
    waiting: [],
  },
  {
    id: "offer",
    title: "3 Offer",
    design: "11 Driver App",
    operation: OFFER_STREAM,
    built: true,
    waiting: [],
  },
  {
    id: "offer-expired",
    title: "3b Offer expired",
    design: "11 Driver App",
    operation: OFFER_STREAM,
    built: true,
    waiting: [],
  },
  {
    id: "to-pickup",
    title: "4 To pickup",
    design: "11 Driver App",
    operation: PICKUP,
    built: true,
    waiting: [],
  },
  {
    id: "scan",
    title: "5 Scan",
    design: "11 Driver App",
    operation: SCAN,
    built: true,
    waiting: [],
  },
  {
    id: "offline-queue",
    title: "6 Offline queue",
    design: "11 Driver App",
    operation: DEVICE_QUEUE,
    built: true,
    waiting: [],
  },
  {
    id: "document-expiring",
    title: "7 Document expiring",
    design: "11 Driver App",
    operation: SHIFT_READINESS,
    built: true,
    waiting: [],
  },
  {
    id: "proof",
    title: "8 Proof",
    design: "11 Driver App",
    operation: PROOF,
    built: true,
    waiting: [],
  },
  {
    id: "earnings-today",
    title: "9 Earnings",
    design: "11 Driver App",
    operation: null,
    built: false,
    waiting: [
      {
        element: "`TZS 9,600 today`",
        needs: "earnings.totalMinor, summary.earningsMinor or earnings.netMinor",
      },
      {
        element: "`Jobs TZS 8,400` and `Busy-area bonus TZS 1,200`",
        needs: "earnings.lines, statement.lines or earnings.components",
      },
      {
        element: "`Commission · shown on your statement`",
        needs: "GET /v1/tenants/{tenantId}/commission-calculations/{calculationId}",
      },
      {
        element: "`next payout Friday` — this driver's own payouts",
        needs: "a payout listing; POST /v1/payouts exists and there is no GET",
      },
    ],
  },
  {
    id: "onboarding-board",
    title: "Onboarding board",
    design: "15 WP14 Driver Onboarding and Earnings",
    /*
     * Two of three closed by one decision, and the third moved rather than closing.
     *
     * **DRV-02 had three writes and no read of any kind.** An application could be created,
     * verified and moved through ten statuses while the applicant had no operation that would
     * tell them any of it — including that a reviewer had marked it `action_required`, which
     * the status vocabulary has always had and which no step could say. ADR 0076 publishes the
     * read and the step reason together, because a board that can say a step needs attention and
     * not what attention is not worth drawing.
     *
     * The third entry is now an absence rather than a blocker. It asked for a step's expected
     * completion; what the design actually draws is `With our partner · 2–3 days`, which is a
     * human estimate rather than a date, and the row it belongs to has no step behind it at all
     * — see the absence below.
     */
    operation: "GET /v1/tenants/{tenantId}/onboarding-applications/{applicationId} (DRV-02)",
    built: true,
    waiting: [],
    /*
     * Three things this screen draws that it will not fill, recorded here because this app's
     * `DriverScreen` has no `absent` field — unlike the rider app's — so the measurement in
     * `tests/contract/mobile-screen-support.test.ts` is where they are graded:
     *
     *   - `Done 26 Sep`, `Approved 27 Sep`, `since 28 Sep` — when each step moved. DRV-02
     *     timestamps the application and not its steps, and `updatedAt` is when the application
     *     last moved for any reason, so it would be wrong on every step but the most recent.
     *   - `PrDP permit` and `Background check` — two of the design's five rows. DRV-02 seeds every
     *     application from three step kinds, so a fourth and fifth row would have no step behind
     *     them and would sit at `pending` forever.
     *   - `With our partner · 2–3 days` — how long an external check will take. Nothing models it,
     *     and the design's own text is an estimate rather than a date.
     */
  },
  {
    id: "vehicle",
    title: "Vehicle",
    design: "15 WP14 Driver Onboarding and Earnings",
    operation: VEHICLE,
    built: true,
    waiting: [],
  },
  {
    id: "document-upload",
    title: "Document upload",
    design: "15 WP14 Driver Onboarding and Earnings",
    operation: DOCUMENT_UPLOAD,
    built: true,
    waiting: [],
  },
  {
    id: "earnings",
    title: "Earnings",
    design: "15 WP14 Driver Onboarding and Earnings",
    operation: EARNINGS,
    built: true,
    waiting: [],
  },
  {
    id: "quality-score",
    title: "Quality score",
    design: "15 WP14 Driver Onboarding and Earnings",
    operation: EARNINGS,
    built: true,
    waiting: [],
  },
  {
    id: "handoff",
    title: "Handoff",
    design: "15 WP14 Driver Onboarding and Earnings",
    /*
     * Two of three closed, and the second entry was pointed at the wrong service entirely.
     *
     * It asked for **a public handoff read**, meaning LOG's last-mile handoff — the saga that
     * turns a shipment arriving at its destination hub into a delivery order. That record holds a
     * shipment, a hub, a state and a delivery order identifier; it has no seal, no bay and no
     * parcel in it, and it was never going to answer this screen however public it became.
     *
     * What the design draws is a **transport manifest**: a sealed collection at a hub with a seal
     * number on the door and a declared count inside. LOG-07 owns that and had published both a
     * listing and a by-identifier read, publicly, all along. What it did not record was what
     * sealing produces — ADR 0077 adds `sealId`, `bay` and `parcelCount`, set by the seal
     * command, which is the physical act those three are facts about.
     *
     * Two things this screen draws that it will not fill, both the same absence:
     *
     *   - `Scanned 11 of 14` and `Missing 3`. A courier's progress against this manifest is
     *     published by nothing. LOG-06 keys a scan to a shipment and a parcel with no manifest on
     *     it and publishes no listing at any tenancy bound; LOG-07 does not see scans. It is
     *     derivable by **neither service alone**, so it is a read model somebody has to decide to
     *     build rather than a field somebody forgot.
     *   - `intact`, on the seal row. Nothing records whether a seal was found broken, and a
     *     screen asserting a seal is intact is the one thing it must not do while the courier is
     *     standing in front of it.
     */
    operation: "GET /v1/tenants/{tenantId}/manifests (LOG-07), filtered to sealed",
    built: true,
    waiting: [],
  },
  {
    id: "multi-stop-job",
    title: "Multi-stop job",
    design: "15 WP14 Driver Onboarding and Earnings",
    operation: STOP_PLAN,
    built: true,
    waiting: [],
  },
];

/** The screens this build draws, which is what the router has a branch for. */
export const builtScreens: readonly DriverScreen[] = driverScreens.filter((screen) => screen.built);

export function screenById(id: string): DriverScreen | undefined {
  return driverScreens.find((screen) => screen.id === id);
}
